import React, { useEffect } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { useSelector } from 'react-redux';
import type { RootState, ThemeMode } from 'sama-shared';

/*
 * Proveedor de tema propio de sama-partner (alcance: solo este paquete).
 * Tres modos: light (crema cálida) · dark (carbón brasa) · ocean (navy,
 * superficies azuladas, MISMO acento brasa para no romper el sistema).
 * Sustituye al ThemeWrapper genérico. Fija `data-sama-theme` en <html>
 * para los overrides CSS ([data-sama-theme='light'] = rama clara).
 */

const COMMON_COMPONENTS = {
  Button: { controlHeight: 40, controlHeightLG: 48, borderRadius: 10, fontWeight: 600 },
  Card: { borderRadiusLG: 20, paddingLG: 20 },
  Table: { headerBg: 'rgba(255,90,0,0.06)', rowHoverBg: 'rgba(255,90,0,0.05)' },
  Menu: { itemBorderRadius: 10, itemHeight: 40, darkItemBg: 'transparent' },
  Tag: { borderRadiusSM: 7 },
  Drawer: { borderRadiusLG: 20 },
  Modal: { borderRadiusLG: 20 },
} as const;

const MODE_META: Record<
  ThemeMode,
  { algorithm: typeof antdTheme.darkAlgorithm; canvas: string; token: Record<string, string> }
> = {
  dark: {
    algorithm: antdTheme.darkAlgorithm,
    canvas: '#100c09',
    token: {
      colorTextBase: '#fff7ed',
      colorBgBase: '#100c09',
      colorBgContainer: '#1e1814',
      colorBgElevated: '#262019',
      colorBgLayout: '#100c09',
      colorBorder: 'rgba(255,214,170,0.09)',
      colorBorderSecondary: 'rgba(255,214,170,0.055)',
      boxShadow: '0 8px 28px -8px rgba(255,90,0,0.35)',
      boxShadowSecondary: '0 4px 16px rgba(0,0,0,0.4)',
    },
  },
  light: {
    algorithm: antdTheme.defaultAlgorithm,
    canvas: '#fff3e6',
    token: {
      colorTextBase: '#221607',
      colorBgBase: '#fff9f2',
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#fff3e6',
      colorBorder: 'rgba(120,72,20,0.12)',
      colorBorderSecondary: 'rgba(120,72,20,0.07)',
      boxShadow: '0 12px 32px -16px rgba(255,90,0,0.25)',
      boxShadowSecondary: '0 4px 16px rgba(120,72,20,0.06)',
    },
  },
  ocean: {
    algorithm: antdTheme.darkAlgorithm,
    canvas: '#0a1222',
    token: {
      colorTextBase: '#eaf2ff',
      colorBgBase: '#0a1222',
      colorBgContainer: '#101a30',
      colorBgElevated: '#16223c',
      colorBgLayout: '#0a1222',
      colorBorder: 'rgba(147,197,253,0.14)',
      colorBorderSecondary: 'rgba(147,197,253,0.08)',
      boxShadow: '0 8px 28px -8px rgba(255,90,0,0.35)',
      boxShadowSecondary: '0 4px 18px rgba(2,8,23,0.6)',
    },
  },
};

export const PartnerThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const currentTheme = useSelector((state: RootState) => state.auth.theme) as ThemeMode;
  const mode: ThemeMode = currentTheme ?? 'dark';
  const meta = MODE_META[mode] ?? MODE_META.dark;

  useEffect(() => {
    document.documentElement.setAttribute('data-sama-theme', mode);
  }, [mode]);

  return (
    <ConfigProvider
      theme={{
        algorithm: meta.algorithm,
        token: {
          colorPrimary: '#ff5a00',
          colorInfo: '#ff5a00',
          colorSuccess: '#5daa1e',
          colorWarning: '#ffb020',
          colorError: '#ff4d5e',
          borderRadius: 10,
          borderRadiusLG: 20,
          borderRadiusSM: 8,
          fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
          ...meta.token,
        },
        components: COMMON_COMPONENTS as any,
      }}
    >
      <div
        style={{
          background: meta.canvas,
          minHeight: '100vh',
          width: '100%',
          transition: 'background 220ms ease',
        }}
      >
        {children}
      </div>
    </ConfigProvider>
  );
};

export default PartnerThemeProvider;
